import React, { useEffect, useMemo, useState } from 'react';
import { InventoryHistoryDialog } from '../InventoryHistoryDialog';
import { CreateInventoryModal } from '../../../components/inventory/CreateInventoryModal';
import { InventoryRoastProfileModal } from '../../../components/inventory/InventoryRoastProfileModal';
import { FinishedGoodsProductionModal } from '../../../components/inventory/FinishedGoodsProductionModal';
import { ImportGreenBeanModal } from '../../../components/inventory/ImportGreenBeanModal';
import { ImportRoastedBulkModal } from '../../../components/inventory/ImportRoastedBulkModal';
import { Modal } from '../../../components/common/Modal';
import { FinishedGoodsTasteProfileForm } from '../FinishedGoodsTasteProfileForm';
import type { InventoryItem } from '../../../types/inventory';
import type { FinishedGoodsProductionResult } from '../../../hooks/inventory-hooks/useFinishedGoodsProductionForm';
import { profileService, type CuppingProfile } from '../../../services/profileService';
import type {
  InventoryTypeFilter,
  RoastProfileContext,
  FinishedGoodsTasteProfileContext,
} from '../../../hooks/inventory-hooks/useInventoryPageState';
import { useLanguage } from '../../../features/language/useLanguage';
import { usePermissions } from '../../../hooks/usePermissions';

interface InventoryModalsProps {
  items: InventoryItem[];
  historyItemId: string | null;
  onCloseHistory: () => void;
  onHistoryUpdated: () => void;
  editingItem: InventoryItem | undefined;
  typeFilter: InventoryTypeFilter;
  isCreateModalOpen: boolean;
  onCloseCreateModal: () => void;
  onCreateSuccess: () => void;
  tasteProfileContext: FinishedGoodsTasteProfileContext | null;
  onCloseTasteProfile: () => void;
  onTasteProfileSuccess: () => Promise<void> | void;
  tasteProfileListItem: InventoryItem | null;
  onCloseTasteProfileList: () => void;
  onOpenTasteProfileList: (item: InventoryItem) => void;
  onSelectTasteProfile: (item: InventoryItem, profileId: string) => void;
  roastProfileContext: RoastProfileContext | null;
  onCloseRoastProfile: () => void;
  onRoastProfileSaved: () => void;
  isFinishedGoodsModalOpen: boolean;
  onCloseFinishedGoodsModal: () => void;
  onFinishedGoodsSuccess: (result: FinishedGoodsProductionResult) => void;
  isImportModalOpen: boolean;
  onCloseImportModal: () => void;
  onImportSuccess: () => Promise<void> | void;
  isImportRBModalOpen: boolean;
  onCloseImportRBModal: () => void;
  onImportRBSuccess: () => Promise<void> | void;
  canWrite?: boolean;
}

export const InventoryModals: React.FC<InventoryModalsProps> = ({
  items,
  historyItemId,
  onCloseHistory,
  onHistoryUpdated,
  editingItem,
  typeFilter,
  isCreateModalOpen,
  onCloseCreateModal,
  onCreateSuccess,
  tasteProfileContext,
  onCloseTasteProfile,
  onTasteProfileSuccess,
  tasteProfileListItem,
  onCloseTasteProfileList,
  onOpenTasteProfileList,
  onSelectTasteProfile,
  roastProfileContext,
  onCloseRoastProfile,
  onRoastProfileSaved,
  isFinishedGoodsModalOpen,
  onCloseFinishedGoodsModal,
  onFinishedGoodsSuccess,
  isImportModalOpen,
  onCloseImportModal,
  onImportSuccess,
  isImportRBModalOpen,
  onCloseImportRBModal,
  onImportRBSuccess,
  canWrite = true,
}) => {
  const { t } = useLanguage();
  const { can } = usePermissions();
  // Taste profile edits hit the taste_profiles resource — gating on inventory:write
  // locks out custom roles granted only taste_profiles:write.
  const canWriteTasteProfile = can('taste_profiles', 'write');
  const [tasteProfiles, setTasteProfiles] = useState<CuppingProfile[]>([]);
  const [tasteProfilesLoading, setTasteProfilesLoading] = useState(false);
  const [tasteProfilesError, setTasteProfilesError] = useState<string | null>(null);
  const selectedHistoryItem = items.find(i => i.id === historyItemId);

  const formatProfileDate = useMemo(
    () => (profile: CuppingProfile) => {
      const dateValue = profile.created_at || profile.cupping_date;
      const date = dateValue ? new Date(dateValue) : null;
      if (!date || Number.isNaN(date.getTime())) {
        return profile.cupping_date || dateValue;
      }
      return date.toLocaleString('id-ID', {
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      });
    },
    []
  );

  useEffect(() => {
    const loadProfiles = async () => {
      if (!tasteProfileListItem?.id) {
        setTasteProfiles([]);
        return;
      }
      setTasteProfilesLoading(true);
      setTasteProfilesError(null);
      try {
        const profiles = await profileService.getCuppingProfileByEntity('ItemType', tasteProfileListItem.id);
        const matching = profiles.filter((candidate) => {
          const extended = candidate.extended_data as Record<string, unknown> | undefined;
          return (
            candidate.cupping_type === 'FinishedGoodsTaste' ||
            extended?.profile_variant === 'finished_goods_simple'
          );
        });
        const sorted = [...matching].sort((a, b) => {
          const dateA = new Date(a.created_at || a.cupping_date).getTime();
          const dateB = new Date(b.created_at || b.cupping_date).getTime();
          return (Number.isNaN(dateB) ? 0 : dateB) - (Number.isNaN(dateA) ? 0 : dateA);
        });
        setTasteProfiles(sorted);
      } catch (err: unknown) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status && status !== 404) {
          setTasteProfilesError(t('inventory.modals.loadTasteProfilesFailed'));
        }
      } finally {
        setTasteProfilesLoading(false);
      }
    };

    loadProfiles();
  }, [tasteProfileListItem?.id, t]);

  return (
    <>
    <InventoryHistoryDialog
      itemId={historyItemId}
      isOpen={!!historyItemId}
      onClose={onCloseHistory}
      onUpdate={onHistoryUpdated}
      currentStock={selectedHistoryItem?.stock}
      itemType={selectedHistoryItem?.itemType}
      roastedWorkOrderReferenceId={selectedHistoryItem?.woReferenceId || selectedHistoryItem?.workOrderId}
      finishedGoodName={selectedHistoryItem?.name}
      readOnly={!canWrite}
    />

    <CreateInventoryModal
      isOpen={isCreateModalOpen && (typeFilter !== 'finished_good' || !!editingItem)}
      onClose={onCloseCreateModal}
      onSuccess={onCreateSuccess}
      itemToEdit={editingItem}
      readOnly={!canWrite}
    />

    {tasteProfileContext && (
      <Modal
        isOpen={!!tasteProfileContext}
        onClose={onCloseTasteProfile}
        title={`${t('inventory.modals.tasteProfileTitlePrefix')} – ${tasteProfileContext.item.name}`}
        maxWidth="4xl"
      >
        <FinishedGoodsTasteProfileForm
          item={tasteProfileContext.item}
          mode={tasteProfileContext.mode}
          existingProfileId={tasteProfileContext.existingProfileId}
          onViewOtherProfiles={() => onOpenTasteProfileList(tasteProfileContext.item)}
          onCancel={onCloseTasteProfile}
          onSuccess={onTasteProfileSuccess}
          readOnly={!canWriteTasteProfile}
        />
      </Modal>
    )}

    {tasteProfileListItem && (
      <Modal
        isOpen={!!tasteProfileListItem}
        onClose={onCloseTasteProfileList}
        title={t('inventory.modals.selectTasteProfile')}
        maxWidth="sm"
      >
        <div className="space-y-2">
          <p className="text-sm text-gray-500 mb-3">
            {t('inventory.modals.itemHasTasteProfiles', { count: tasteProfiles.length })}
          </p>
          {tasteProfilesLoading ? (
            <div className="flex items-center justify-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : tasteProfilesError ? (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-700">{tasteProfilesError}</div>
          ) : (
            <div className="space-y-1 max-h-64 overflow-y-auto">
              {tasteProfiles.map((profile) => (
                <button
                  key={profile.id}
                  type="button"
                  onClick={() => onSelectTasteProfile(tasteProfileListItem, profile.id)}
                  className="w-full text-left px-4 py-3 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors flex items-center justify-between"
                >
                  <div>
                    <div className="text-sm font-medium text-gray-900">{formatProfileDate(profile)}</div>
                    <div className="text-xs text-gray-500">{t('inventory.modals.finishedGoodsTasteProfile')}</div>
                  </div>
                  <svg className="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          )}
        </div>
      </Modal>
    )}

    {roastProfileContext && (
      <InventoryRoastProfileModal
        isOpen={!!roastProfileContext}
        onClose={onCloseRoastProfile}
        title={`${t('inventory.modals.roastProfileTitlePrefix')} – ${roastProfileContext.itemName}`}
        workOrderId={roastProfileContext.workOrderId}
        referenceId={!roastProfileContext.workOrderId ? roastProfileContext.itemId : undefined}
        onSaved={onRoastProfileSaved}
        readOnly={!canWrite}
      />
    )}

    <FinishedGoodsProductionModal
      isOpen={isFinishedGoodsModalOpen}
      onClose={onCloseFinishedGoodsModal}
      onSuccess={onFinishedGoodsSuccess}
      canWrite={canWrite}
    />

    <ImportGreenBeanModal
      isOpen={isImportModalOpen}
      onClose={onCloseImportModal}
      onSuccess={() => { void onImportSuccess(); }}
    />

    <ImportRoastedBulkModal
      isOpen={isImportRBModalOpen}
      onClose={onCloseImportRBModal}
      onSuccess={() => { void onImportRBSuccess(); }}
    />
    </>
  );
};
